
import React, { useState, useEffect } from 'react'
import { X, User, FileText, MapPin, Briefcase, DollarSign, CreditCard } from 'lucide-react'

interface FuncionarioFormProps {
  isOpen: boolean
  onClose: () => void
  onSubmit: (data: any) => Promise<void>
  initialData?: any
  title: string
}

export const FuncionarioForm: React.FC<FuncionarioFormProps> = ({
  isOpen,
  onClose,
  onSubmit,
  initialData,
  title
}) => {
  const [formData, setFormData] = useState({
    nome: '',
    cpf: '',
    rg: '',
    dataAdmissao: '',
    telefone: '',
    email: '',
    endereco: {
      rua: '',
      numero: '',
      bairro: '',
      cidade: '',
      estado: '',
      cep: ''
    },
    cargo: '',
    funcao: '',
    remuneracao: 0,
    unidadeRemuneracao: 'mes' as 'hora' | 'dia' | 'mes',
    valorRemuneracao: 0,
    diaPagamento: 5,
    dadosBancarios: {
      tipoPagamento: 'transferencia' as 'transferencia' | 'pix',
      banco: '',
      agencia: '',
      conta: '',
      chavePix: '',
      tipoPix: 'cpf' as 'cpf' | 'email' | 'telefone' | 'chave_aleatoria'
    },
    bancoHoras: 0,
    status: 'ativo' as 'ativo' | 'ferias' | 'inativo'
  })

  const [activeTab, setActiveTab] = useState<'pessoais' | 'endereco' | 'profissional' | 'bancario'>('pessoais')
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (initialData) {
      setFormData({
        ...initialData,
        dataAdmissao: initialData.dataAdmissao ? initialData.dataAdmissao.split('T')[0] : '',
        endereco: initialData.endereco || {
          rua: '',
          numero: '',
          bairro: '',
          cidade: '',
          estado: '',
          cep: ''
        },
        dadosBancarios: initialData.dadosBancarios || {
          tipoPagamento: 'transferencia',
          banco: '',
          agencia: '',
          conta: '',
          chavePix: '',
          tipoPix: 'cpf'
        }
      })
    } else {
      setFormData({
        nome: '',
        cpf: '',
        rg: '',
        dataAdmissao: '',
        telefone: '',
        email: '',
        endereco: {
          rua: '',
          numero: '',
          bairro: '',
          cidade: '',
          estado: '',
          cep: ''
        },
        cargo: '',
        funcao: '',
        remuneracao: 0,
        unidadeRemuneracao: 'mes',
        valorRemuneracao: 0,
        diaPagamento: 5,
        dadosBancarios: {
          tipoPagamento: 'transferencia',
          banco: '',
          agencia: '',
          conta: '',
          chavePix: '',
          tipoPix: 'cpf'
        },
        bancoHoras: 0,
        status: 'ativo'
      })
    }
  }, [initialData, isOpen])

  const handleInputChange = (field: string, value: any) => {
    if (field.includes('.')) {
      const [parent, child] = field.split('.')
      setFormData(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent as keyof typeof prev],
          [child]: value
        }
      }))
    } else {
      setFormData(prev => ({
        ...prev,
        [field]: value
      }))
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)

    try {
      const submitData = {
        ...formData,
        dataAdmissao: new Date(formData.dataAdmissao).toISOString(),
        remuneracao: Number(formData.valorRemuneracao),
        valorRemuneracao: Number(formData.valorRemuneracao),
        diaPagamento: Number(formData.diaPagamento),
        bancoHoras: Number(formData.bancoHoras)
      }

      await onSubmit(submitData)
      onClose()
    } catch (error) {
      console.error('Erro ao salvar funcionário:', error)
    } finally {
      setLoading(false)
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b p-6 z-10">
          <div className="flex justify-between items-center">
            <h2 className="text-xl font-bold text-gray-900">{title}</h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <X size={24} />
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit}>
          {/* Tabs */}
          <div className="border-b border-gray-200">
            <nav className="flex space-x-8 px-6">
              {[
                { id: 'pessoais', label: 'Dados Pessoais', icon: User },
                { id: 'endereco', label: 'Endereço', icon: MapPin },
                { id: 'profissional', label: 'Dados Profissionais', icon: Briefcase },
                { id: 'bancario', label: 'Dados Bancários', icon: CreditCard }
              ].map(({ id, label, icon: Icon }) => (
                <button
                  key={id}
                  type="button"
                  onClick={() => setActiveTab(id as any)}
                  className={`py-4 px-2 border-b-2 font-medium text-sm flex items-center gap-2 ${
                    activeTab === id
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <Icon size={16} />
                  {label}
                </button>
              ))}
            </nav>
          </div>

          <div className="p-6">
            {/* Dados Pessoais */}
            {activeTab === 'pessoais' && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Nome Completo *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.nome}
                    onChange={(e) => handleInputChange('nome', e.target.value)}
                    className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    CPF *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.cpf}
                    onChange={(e) => handleInputChange('cpf', e.target.value)}
                    placeholder="000.000.000-00"
                    className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    RG *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.rg}
                    onChange={(e) => handleInputChange('rg', e.target.value)}
                    placeholder="00.000.000-0"
                    className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Data de Admissão *
                  </label>
                  <input
                    type="date"
                    required
                    value={formData.dataAdmissao}
                    onChange={(e) => handleInputChange('dataAdmissao', e.target.value)}
                    className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Telefone *
                  </label>
                  <input
                    type="tel"
                    required
                    value={formData.telefone}
                    onChange={(e) => handleInputChange('telefone', e.target.value)}
                    placeholder="(11) 99999-9999"
                    className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    E-mail *
                  </label>
                  <input
                    type="email"
                    required
                    value={formData.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>
            )}

            {/* Endereço */}
            {activeTab === 'endereco' && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Rua *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.endereco.rua}
                    onChange={(e) => handleInputChange('endereco.rua', e.target.value)}
                    className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Número *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.endereco.numero}
                    onChange={(e) => handleInputChange('endereco.numero', e.target.value)}
                    className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Bairro *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.endereco.bairro}
                    onChange={(e) => handleInputChange('endereco.bairro', e.target.value)}
                    className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Cidade *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.endereco.cidade}
                    onChange={(e) => handleInputChange('endereco.cidade', e.target.value)}
                    className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Estado *
                  </label>
                  <select
                    required
                    value={formData.endereco.estado}
                    onChange={(e) => handleInputChange('endereco.estado', e.target.value)}
                    className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="">Selecione o estado</option>
                    <option value="SP">São Paulo</option>
                    <option value="RJ">Rio de Janeiro</option>
                    <option value="MG">Minas Gerais</option>
                    <option value="RS">Rio Grande do Sul</option>
                    <option value="PR">Paraná</option>
                    <option value="SC">Santa Catarina</option>
                    <option value="BA">Bahia</option>
                    <option value="GO">Goiás</option>
                    <option value="PE">Pernambuco</option>
                    <option value="CE">Ceará</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    CEP *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.endereco.cep}
                    onChange={(e) => handleInputChange('endereco.cep', e.target.value)}
                    placeholder="00000-000"
                    className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>
            )}

            {/* Dados Profissionais */}
            {activeTab === 'profissional' && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Cargo *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.cargo}
                    onChange={(e) => handleInputChange('cargo', e.target.value)}
                    className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Função *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.funcao}
                    onChange={(e) => handleInputChange('funcao', e.target.value)}
                    className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Unidade de Remuneração *
                  </label>
                  <select
                    required
                    value={formData.unidadeRemuneracao}
                    onChange={(e) => handleInputChange('unidadeRemuneracao', e.target.value)}
                    className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="hora">Por Hora</option>
                    <option value="dia">Por Dia</option>
                    <option value="mes">Por Mês</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Valor da Remuneração *
                  </label>
                  <input
                    type="number"
                    required
                    min="0"
                    step="0.01"
                    value={formData.valorRemuneracao}
                    onChange={(e) => handleInputChange('valorRemuneracao', Number(e.target.value))}
                    className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Dia de Pagamento *
                  </label>
                  <input
                    type="number"
                    required
                    min="1"
                    max="31"
                    value={formData.diaPagamento}
                    onChange={(e) => handleInputChange('diaPagamento', Number(e.target.value))}
                    className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Banco de Horas
                  </label>
                  <input
                    type="number"
                    step="0.5"
                    value={formData.bancoHoras}
                    onChange={(e) => handleInputChange('bancoHoras', Number(e.target.value))}
                    className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Status
                  </label>
                  <select
                    value={formData.status}
                    onChange={(e) => handleInputChange('status', e.target.value)}
                    className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="ativo">Ativo</option>
                    <option value="ferias">Em Férias</option>
                    <option value="inativo">Inativo</option>
                  </select>
                </div>
              </div>
            )}

            {/* Dados Bancários */}
            {activeTab === 'bancario' && (
              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Tipo de Pagamento
                  </label>
                  <select
                    value={formData.dadosBancarios.tipoPagamento}
                    onChange={(e) => handleInputChange('dadosBancarios.tipoPagamento', e.target.value)}
                    className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="transferencia">Transferência Bancária</option>
                    <option value="pix">PIX</option>
                  </select>
                </div>

                {formData.dadosBancarios.tipoPagamento === 'transferencia' && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Banco
                      </label>
                      <input
                        type="text"
                        value={formData.dadosBancarios.banco}
                        onChange={(e) => handleInputChange('dadosBancarios.banco', e.target.value)}
                        className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Agência
                      </label>
                      <input
                        type="text"
                        value={formData.dadosBancarios.agencia}
                        onChange={(e) => handleInputChange('dadosBancarios.agencia', e.target.value)}
                        className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>

                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Conta
                      </label>
                      <input
                        type="text"
                        value={formData.dadosBancarios.conta}
                        onChange={(e) => handleInputChange('dadosBancarios.conta', e.target.value)}
                        className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                  </div>
                )}

                {formData.dadosBancarios.tipoPagamento === 'pix' && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Tipo de Chave PIX
                      </label>
                      <select
                        value={formData.dadosBancarios.tipoPix}
                        onChange={(e) => handleInputChange('dadosBancarios.tipoPix', e.target.value)}
                        className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      >
                        <option value="cpf">CPF</option>
                        <option value="email">E-mail</option>
                        <option value="telefone">Telefone</option>
                        <option value="chave_aleatoria">Chave Aleatória</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Chave PIX
                      </label>
                      <input
                        type="text"
                        value={formData.dadosBancarios.chavePix}
                        onChange={(e) => handleInputChange('dadosBancarios.chavePix', e.target.value)}
                        placeholder={
                          formData.dadosBancarios.tipoPix === 'cpf' ? '000.000.000-00' :
                          formData.dadosBancarios.tipoPix === 'email' ? 'email@exemplo.com' :
                          formData.dadosBancarios.tipoPix === 'telefone' ? '(11) 99999-9999' :
                          'Chave aleatória'
                        }
                        className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="sticky bottom-0 bg-white border-t p-6 flex justify-end gap-4">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
            >
              {loading ? 'Salvando...' : 'Salvar Funcionário'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default FuncionarioForm
